import {css, StyleSheet} from 'aphrodite';
import React, {useMemo, useState} from 'react';

import {assert} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BaseCreateModalContents} from 'folder-items/BaseCreateModal';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {calcTagTableErrorsByFieldID, useTagTableState} from 'metadata/useTagTableState';
import {
  isCreationDisabled,
  useInitialEntryDay,
} from 'notebook/entry-templates/CreateEntryFromTemplateModal/CreateEntryFromTemplateModal';
import CreateEntryFromTemplateModalContents from 'notebook/entry-templates/CreateEntryFromTemplateModal/CreateEntryFromTemplateModalContents';
import {useEntryTemplate} from 'notebook/entry-templates/hooks/useEntryTemplate';
import {getTagsToSet} from 'schemas/tags/TagUtil';
import {TemplateNode} from 'studies/sidepanel/CreateStudyModal';
import {CreateStudyModalOptionalParams} from 'studies/sidepanel/NewStudyDropdown';
import {DesignEntryInformation, DesignEntryType} from 'studies/StudyDAO';
import {
  CustomStudyState,
  DesignEntryDraftParams,
  StudySchema,
  TypedStudyAdapter,
} from 'studies/typed/utils/TypedStudyTypes';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {ButtonSize} from 'ui-library/buttons/Button';
import {Header2} from 'ui-library/Headers';
import ToggleGroup from 'ui-library/ToggleGroup';
import strUtil from 'util/strUtil';

import CustomStudyDesignOverview from './CustomStudyDesignOverview';
import CustomStudyExecutionOverview from './CustomStudyExecutionOverview';

const DEFAULT_ENTRY_DRAFT_NAME = 'Untitled';

/**
 * For custom studies, we have a lightweight concept of an experiment, which is just a design entry.
 */
function getExperimentParam(entryInfo: DesignEntryDraftParams): DesignEntryInformation | null {
  let finalizedEntryInfo: DesignEntryInformation | null;

  // Validate entry params
  assert(entryInfo != null, 'Custom studies must have a design entry');
  if (
    !entryInfo.folder ||
    strUtil.isEmpty(entryInfo.name) ||
    Object.entries(entryInfo.errorsByFieldId).length > 0
  ) {
    return null;
  }
  const entryTagUpdateObjects = getTagsToSet(entryInfo.schemaTags);

  // Validate entry template params
  if (entryInfo.designEntryType === DesignEntryType.FROM_TEMPLATE) {
    if (
      isCreationDisabled(
        entryInfo.templateId,
        entryInfo.name,
        entryInfo.folder,
        entryInfo.templateQueryResultType,
        entryInfo.tagSchemaId != null,
        entryInfo.errorsByFieldId
      )
    ) {
      return null;
    }

    finalizedEntryInfo = {
      type: DesignEntryType.FROM_TEMPLATE,
      name: entryInfo.name,
      folder: entryInfo.folder,
      tagSchemaId: entryInfo.tagSchemaId,
      schemaTags: entryTagUpdateObjects,
      template: entryInfo.templateId!,
      initialDay: entryInfo.initialDay,
    };
  } else {
    assert(entryInfo.designEntryType === DesignEntryType.BLANK, 'Received unexpected designEntryType');
    finalizedEntryInfo = {
      type: DesignEntryType.BLANK,
      name: entryInfo.name,
      folder: entryInfo.folder,
      tagSchemaId: entryInfo.tagSchemaId,
      schemaTags: entryTagUpdateObjects,
    };
  }

  return finalizedEntryInfo;
}

export const CustomStudyAdapter: TypedStudyAdapter<'entryInformation', CustomStudyState> = {
  type: StudyType.CUSTOM,
  experimentKey: 'entryInformation',

  isMultiProcedureRunEnabled: false,

  isMultiBatchEnabled: false,

  isMultiConditionEnabled: false,

  isVisualizationEnabled: false,

  isOOTBChartingEnabled: false,

  maxRunsPerStudy: () => null,

  isNotificationSchedulesEnabled: true,

  isUnitOperationInstanceLinkingEnabled: false,

  allowReturnToDesignPhase: true,

  allowRequireDesignApproval: true,

  renderMetadataSection(state: CustomStudyState): JSX.Element {
    return (
      <>
        <Row className={css(styles.header)}>
          <Col sm={12}>
            <Header2>Study design entry</Header2>
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <p>
              Any items created in your study design will be automatically linked to your study, if you have
              permission to do so.
            </p>
          </Col>
        </Row>
        <Row>
          <Col sm={12}>
            <ToggleGroup<DesignEntryType>
              onChangeSelection={state.setDesignEntryType}
              selection={state.entryInformation.designEntryType}
              size={ButtonSize.MEDIUM}
              options={[
                {
                  id: DesignEntryType.FROM_TEMPLATE,
                  buttonProps: {text: 'From template'},
                },
                {
                  id: DesignEntryType.BLANK,
                  buttonProps: {text: 'Blank entry'},
                },
              ]}
            />
          </Col>
        </Row>
        {state.entryInformation.designEntryType === DesignEntryType.FROM_TEMPLATE ? (
          <CreateEntryFromTemplateModalContents
            isTemplateSelectorDisabled={false}
            templateId={state.entryInformation.templateId}
            changeTemplate={state.changeTemplate}
            initialDay={state.entryInformation.initialDay}
            setInitialDay={state.setInitialDay}
            entryName={state.entryInformation.name}
            setEntryName={state.setEntryName}
            folderId={state.entryInformation.folder}
            setFolderId={state.setEntryFolderId}
            folderFieldLabel="Design entry project folder"
            // CreateStudyModal only renders after the query has completed
            queryIsPending={false}
            canChangeSchema={state.canChangeSchema}
            schemaTags={state.entryInformation.schemaTags}
            tagSchema={state.entrySchema}
            updateTagSchema={state.updateEntryTagSchema}
            updateSchemaTag={state.updateEntrySchemaTag}
            errorsByFieldId={state.entryInformation.errorsByFieldId}
            filterTemplateIds={state.entryTemplateIds}
          />
        ) : (
          <BaseCreateModalContents
            nameFieldLabel="Entry name"
            folderFieldLabel="Design entry project folder"
            name={state.entryInformation.name}
            setName={state.setEntryName}
            modelType={ModelTypeConstants.ENTRY}
            templateCollection={undefined}
            setTemplateCollection={() => {}}
            showInventory={false}
            showNotebook={true}
            folderId={state.entryInformation.folder}
            setFolderId={(folderId) => {
              state.setEntryFolderId(folderId);
            }}
            tagSchema={state.entrySchema}
            updateTagSchema={state.updateEntryTagSchema}
            updateSchemaTag={state.updateEntrySchemaTag}
            schemaTags={state.entryInformation.schemaTags}
            errorsByFieldId={state.entryInformation.errorsByFieldId}
            selectNameFieldInitially={false}
            entryProvenance="entry"
          />
        )}
      </>
    );
  },

  useTypedStudyState(this: void, studySchema: StudySchema, initialValues: CreateStudyModalOptionalParams) {
    const [entryName, setEntryName] = useState(DEFAULT_ENTRY_DRAFT_NAME);
    const [entryFolderId, setEntryFolderId] = useState(FolderDefaults.get(FolderDefaultsKey.NOTEBOOK));

    const {
      schemaTags: entrySchemaTags,
      tagSchema: entrySchema,
      updateSchemaTag: updateEntrySchemaTag,
      updateTagSchema: updateEntryTagSchema,
    } = useTagTableState();

    const [designEntryType, setDesignEntryType] = useState<DesignEntryType>(DesignEntryType.FROM_TEMPLATE);
    const {templateId, changeTemplate, templateResult, canChangeSchema} = useEntryTemplate(
      null,
      setEntryName,
      updateEntryTagSchema
    );
    const {initialDay, setInitialDay} = useInitialEntryDay();

    const errorsByFieldId = useMemo(
      () => (entrySchema ? calcTagTableErrorsByFieldID(entrySchema, entrySchemaTags) : {}),
      [entrySchema, entrySchemaTags]
    );

    const accessibleEntryTemplates = studySchema.entryTemplates.filter(
      (template) => !isInaccessible(template)
    );
    const entryTemplateIds = accessibleEntryTemplates.map((template: TemplateNode) => template.id);

    const entryInformation = useMemo(
      () =>
        ({
          name: entryName,
          folder: entryFolderId,
          initialDay,
          templateId,
          designEntryType,
          schemaTags: entrySchemaTags,
          tagSchemaId: entrySchema?.id,
          errorsByFieldId,
          templateQueryResultType: templateResult.type,
        }) satisfies DesignEntryDraftParams,
      [
        designEntryType,
        entryFolderId,
        entryName,
        entrySchema?.id,
        entrySchemaTags,
        errorsByFieldId,
        initialDay,
        templateId,
        templateResult.type,
      ]
    );
    const experimentParam = useMemo(() => getExperimentParam(entryInformation), [entryInformation]);
    return {
      entryInformation,
      entryTemplateIds,
      entrySchema,
      setEntryName,
      setEntryFolderId,
      setInitialDay,
      setDesignEntryType,
      updateEntrySchemaTag,
      updateEntryTagSchema,
      changeTemplate,
      canChangeSchema,
      experimentParam,
    };
  },

  getPerformanceMetadata(state: CustomStudyState) {
    return {};
  },

  getAnalyticsMetadata(state: CustomStudyState) {
    return {};
  },
  creation: {
    experimentParamRequired: true,
  },
  design: {
    ui: {
      tab: null,
    },
  },
  overview: {
    ui: {
      tab: null,
      execution: CustomStudyExecutionOverview,
      design: CustomStudyDesignOverview,
    },
  },
  review: {},
};

const styles = StyleSheet.create({
  header: {
    paddingTop: '10px',
  },
});
